import React from "react";
import { formatSecondsBrief, pct } from "../../../../utils/oversightFormat";

/** One label/value row in the warm secondary column. */
function Row({ label, value, tone = "neutral", sub }) {
  const valueClass = tone === "bad" ? "text-bc-bad" : "text-bc-text-2";
  return (
    <div className="flex items-baseline justify-between gap-2 pb-3 border-b border-bc-hairline last:border-b-0 last:pb-0">
      <span className="text-xs text-bc-muted">{label}</span>
      <span className={`text-lg font-bold ${valueClass}`}>
        {value}
        {sub && <span className="ml-1 text-[11px] font-semibold text-bc-soft">{sub}</span>}
      </span>
    </div>
  );
}

/** Nulls read as a dash: "not measured" and "measured zero" are different answers. */
const dash = (v, fmt = (x) => x) => (v == null ? "—" : fmt(v));

/**
 * The Support desk column — ticket work, judged on its own targets and never
 * mixed into the chat numbers.
 *
 * The separation is the point, not a styling choice. `median_first_response_seconds`
 * here is time to answer a TICKET, taken from the SLA record; the
 * identically-named field on the coach analytics payload is time to reply in a
 * CHAT room, computed from message bursts. Ticket CSAT is attributed to the
 * assigned agent; the coach rollup's CSAT is attributed by room access. Reusing
 * either would put a different measurement under this heading.
 *
 * Rendered in the warm `bc-*` palette rather than the brand orange so it reads
 * as secondary at a glance.
 *
 * Presentational: the fetch lives in `useCoachSupportMetrics`, owned by the page
 * shell, so returning to this tab does not re-request and the call goes out in
 * parallel with the page's other loads rather than after them.
 *
 * @param {Object} props
 * @param {Object|null} props.totals - Support metrics, or null.
 * @param {boolean} props.loading
 * @param {'forbidden'|'gateway'|'other'|null} props.errorKind
 */
export function SupportDeskCard({ totals, loading, errorKind }) {
  const touched = totals?.tickets_touched;
  const noTicketWork = !loading && !errorKind && totals != null && touched == null;

  return (
    <div className="rounded-xl border border-gray-200 bg-bc-subtle p-5">
      <div className="flex items-baseline gap-2 mb-1">
        <h2 className="text-sm font-bold text-bc-text-2">Support desk</h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-bc-neut-bg text-bc-muted">
          Secondary
        </span>
      </div>
      <p className="mb-4 text-[11px] text-bc-soft">
        Ticket work, judged on its own targets. Never mixed into the chat numbers.
      </p>

      {loading ? (
        <div className="flex flex-col gap-3.5" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-4 rounded bg-bc-hairline animate-pulse" />
          ))}
        </div>
      ) : errorKind ? (
        <p className="text-xs text-bc-muted">
          {errorKind === "forbidden"
            ? "This coach's ticket work is outside your report chain."
            : "Couldn't load ticket work."}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3.5">
            <Row label="Support tickets touched" value={dash(touched)} />
            <Row label="Resolved" value={dash(totals?.tickets_resolved)} />
            <Row
              label="Usual first reply"
              value={dash(totals?.median_first_response_seconds, formatSecondsBrief)}
            />
            <Row
              label="Usual time to close"
              value={dash(totals?.median_resolution_seconds, formatSecondsBrief)}
            />
            <Row
              label="Came back after closing"
              value={dash(totals?.reopened_count)}
              tone={totals?.reopened_count > 0 ? "bad" : "neutral"}
              // Null when nothing was resolved: a rate over zero resolutions
              // would report a perfect record for a coach who closed nothing.
              sub={totals?.reopen_rate != null ? `(${pct(totals.reopen_rate)})` : undefined}
            />
            <Row
              label="Ticket rating, out of 5"
              value={dash(totals?.csat_avg, (v) => v.toFixed(1))}
              sub={totals?.csat_count ? `${totals.csat_count} rated` : undefined}
            />
          </div>

          {noTicketWork && (
            <p className="mt-5 text-[11px] text-bc-soft">
              No ticket work in this period. These are tickets, not coaching chats — a coach who
              only works chat will always read as blank here.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default SupportDeskCard;
